import { useEffect, useRef, useState } from "react";
import { pdfjs } from "react-pdf";
import { useReactToPrint } from "react-to-print";

import { useServiceClient } from "@megaron/react-clients";
import { MessageAttachmentPayloadDto, TicketDetailDto } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";

import { decodeBase64ToUint8Array } from "./supportTicketAttachmentDecode";

if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

export type ProcessedAttachment = MessageAttachmentPayloadDto & {
  pdfPageImages: string[];
};

export type AttachmentsMap = Map<Uuid, ProcessedAttachment[]>;

const renderPdfToImages = async (contentBase64: string): Promise<string[]> => {
  const bytes = decodeBase64ToUint8Array(contentBase64);
  const pdf = await pdfjs.getDocument({ data: bytes }).promise;
  const images: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d");
    if (ctx && canvas.width > 0 && canvas.height > 0) {
      try {
        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch {
        // partial render — capture what's available
      }
      images.push(canvas.toDataURL("image/jpeg", 0.85));
    }
  }
  return images;
};

export const useSupportTicketPdf = (ticket: TicketDetailDto) => {
  const supportClient = useServiceClient("support");
  const [isLoading, setIsLoading] = useState(false);
  const [attachmentsMap, setAttachmentsMap] = useState<AttachmentsMap>(new Map());
  const [triggerPrint, setTriggerPrint] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: ticket.subject,
  });

  useEffect(() => {
    if (!triggerPrint) return;
    setTriggerPrint(false);
    handlePrint();
  }, [triggerPrint, handlePrint]);

  const fetchAndPrint = async () => {
    setIsLoading(true);
    const map: AttachmentsMap = new Map();

    for (const message of ticket.messages) {
      const fetched: ProcessedAttachment[] = [];
      for (const att of message.attachments) {
        const result = await supportClient.getTicketMessageAttachment({
          id: ticket.id,
          messageId: message.id,
          attachmentId: att.id,
        });
        if (!result.isFailure) {
          const payload = result.value;
          const pdfPageImages =
            payload.fileType === "application/pdf" ? await renderPdfToImages(payload.contentBase64) : [];
          fetched.push({ ...payload, pdfPageImages });
        }
      }
      map.set(message.id, fetched);
    }

    setAttachmentsMap(map);
    setIsLoading(false);
    setTriggerPrint(true);
  };

  return { fetchAndPrint, isLoading, printRef, attachmentsMap };
};
